import { Spinner } from '@aus-platform/design-system';
import { isNil, isUndefined } from 'lodash';
import React from 'react';
import { getUTMCoordinates } from 'src/shared/helpers';
import { type SpotInfoCoordinateDisplay } from 'src/shared/types';
import { CoordinateSystem, EPSGCode } from 'src/shared/enums';
import { type SpotInfoState } from './types';
import { CopyableText } from 'src/shared/components';

type Map3DSpotToolProps = {
  spotInfo: SpotInfoState;
  isAltitudeFetching: boolean;
  isTerrainSelected: boolean;
  coordinateSystem: CoordinateSystem;
};

export const Map3DSpotTool: React.FC<Map3DSpotToolProps> = ({
  spotInfo,
  isAltitudeFetching,
  isTerrainSelected,
  coordinateSystem,
}) => {
  // Renderers.
  const renderAltitude = (altitude: number | null) => {
    return (
      <span data-testid="spot-measurement-altitude">
        {isAltitudeFetching ? (
          <Spinner />
        ) : !isTerrainSelected ? (
          <span>No terrain selected</span>
        ) : isNil(altitude) || !isFinite(altitude) ? (
          <span>Out of Bounds</span>
        ) : (
          <CopyableText
            className="spot-measurement-box__info__value"
            value={altitude.toFixed(2)}
            iconSize={16}
          />
        )}
      </span>
    );
  };

  const renderCoordinates = (coords: SpotInfoCoordinateDisplay) => {
    return (
      <div className="spot-measurement-box">
        {!isUndefined(coords.zoneLabel) && !isUndefined(coords.zoneValue) && (
          <span className="spot-measurement-box__info">
            {coords.zoneLabel}{' '}
            <span data-testid="spot-measurement-utm-zone">
              {coords.zoneValue}
            </span>
          </span>
        )}

        <span className="spot-measurement-box__info">
          {coords.yLabel}{' '}
          {coords.yValue !== '-' ? (
            <CopyableText
              className="spot-measurement-box__info__value"
              value={coords.yValue}
              iconSize={16}
            />
          ) : (
            <span className="spot-measurement-box__info__value">
              {coords.yValue}
            </span>
          )}
        </span>

        <span className="spot-measurement-box__info">
          {coords.xLabel}{' '}
          {coords.xValue !== '-' ? (
            <CopyableText
              className="spot-measurement-box__info__value"
              value={coords.xValue}
              iconSize={16}
            />
          ) : (
            <span className="spot-measurement-box__info__value">
              {coords.xValue}
            </span>
          )}
        </span>

        <span className="spot-measurement-box__info">
          Altitude (m)
          {renderAltitude(spotInfo.altitude)}
        </span>
      </div>
    );
  };

  // Render.
  if (coordinateSystem === CoordinateSystem.UTM) {
    if (isNil(spotInfo.longitude) || isNil(spotInfo.latitude)) {
      return null;
    }

    const { zoneNumber, hemisphere, utmCoords } = getUTMCoordinates(
      spotInfo.longitude,
      spotInfo.latitude,
      EPSGCode.WGS84,
    );

    return renderCoordinates({
      zoneLabel: 'Zone',
      zoneValue: zoneNumber !== -1 ? `${zoneNumber} ${hemisphere}` : '-',
      xLabel: 'Easting',
      xValue: utmCoords.easting ?? '-',
      yLabel: 'Northing',
      yValue: utmCoords.northing ?? '-',
    });
  }

  return renderCoordinates({
    xLabel: 'Longitude',
    xValue: spotInfo.longitude?.toString() ?? '-',
    yLabel: 'Latitude',
    yValue: spotInfo.latitude?.toString() ?? '-',
  });
};
